import {performanceObserverConfiguration} from '../../performanceObserverConfiguration';
import type {PerfWindowTrigger} from './types';

export interface TriggerSettings {
  enabled: boolean;
  minIntervalMs: number;
  /** Window length fixed by config; undefined lets the caller choose. */
  windowDurationMs: number | undefined;
  /** Emit a `started` sampling vital so lost reports can be detected. */
  recordStarted: boolean;
  /** Report active windows before the RUM view changes so they attribute to the old view. */
  interruptOnViewChange: boolean;
}

/** Reads current config for one trigger. Running measurements keep the settings they started with. */
export function getTriggerSettings(
  trigger: PerfWindowTrigger
): TriggerSettings {
  const config = performanceObserverConfiguration.getState();
  switch (trigger) {
    case 'interaction':
      return {
        enabled: config.instrumentInteractions,
        minIntervalMs: config.minIntervalInteraction * 1000,
        windowDurationMs: config.windowInteraction * 1000,
        recordStarted: true,
        interruptOnViewChange: true,
      };
    case 'navigation':
      return {
        enabled: config.instrumentNavigation,
        minIntervalMs: config.minIntervalNavigation * 1000,
        windowDurationMs: config.windowNavigation * 1000,
        recordStarted: false,
        interruptOnViewChange: false,
      };
    case 'workflow':
      return {
        enabled: config.instrumentWorkflows,
        minIntervalMs: config.minIntervalWorkflows * 1000,
        windowDurationMs: undefined,
        recordStarted: false,
        interruptOnViewChange: false,
      };
  }
}

export const observationKey = (
  trigger: PerfWindowTrigger,
  workflow: string
): string => `${trigger}:${workflow}`;

/** Cooldowns are keyed by trigger and workflow, shared by windows and sessions of the same pair. */
export function createSamplingPolicy() {
  const lastStarts = new Map<string, number>();

  /** Checks cooldown without starting the cooldown. Pass performance.now() as now. */
  function isCooldownElapsed(
    trigger: PerfWindowTrigger,
    workflow: string,
    now: number,
    minIntervalMs: number
  ): boolean {
    const lastStart = lastStarts.get(observationKey(trigger, workflow));
    return now - (lastStart ?? -Infinity) >= minIntervalMs;
  }

  /** Starts the cooldown after measurement setup succeeds; failed attempts must remain retryable. */
  function recordStart(
    trigger: PerfWindowTrigger,
    workflow: string,
    now: number
  ): void {
    lastStarts.set(observationKey(trigger, workflow), now);
  }

  return {
    isCooldownElapsed,
    recordStart,
  };
}

export type SamplingPolicy = ReturnType<typeof createSamplingPolicy>;
